import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";
import api from "../lib/api";
import { fmtMoney, fmtNum } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export default function DealCalculation() {
  const { data: deals = [] } = useQuery({ queryKey: ["deals"], queryFn: () => api.get("/dashboard/deal-calculation") });
  const [sel, setSel] = useState(null);

  const cols = [
    { key: "trade_no", label: "Trade No.", render: (r) => <span className="italic text-slate-600 dark:text-slate-300">{r.trade_no}</span> },
    { key: "trader", label: "Trader / Buyer", render: (r) => <span className="text-sm">{r.trader}</span> },
    { key: "commodity", label: "Commodity", render: (r) => <span className="font-semibold uppercase text-xs">{r.commodity}</span> },
    { key: "sales_contract_number", label: "Sales Contract #" },
    { key: "purchase_contract_number", label: "Purchase Contract #" },
    { key: "total_purchase_contract_value", label: "Total Purchase Value", align: "right", render: (r) => fmtNum(r.total_purchase_contract_value, 3) },
    { key: "total_sales_contract_value", label: "Total Sales Value", align: "right", render: (r) => fmtNum(r.total_sales_contract_value, 3) },
    { key: "lc_number", label: "LC Number", render: (r) => <span className="font-mono text-xs">{r.lc_number}</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "_a", label: "Actions", render: (r) => (
      <button data-testid={`deal-actions-${r.trade_no}`} onClick={(e) => { e.stopPropagation(); setSel(r); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
        <MoreVertical className="h-4 w-4 text-slate-400" />
      </button>
    ) },
  ];

  return (
    <div data-testid="deal-calculation-page">
      <DataTable
        testid="deal-calc-table"
        title="Deal Calculation"
        columns={cols}
        rows={deals}
        rowKey="sale_contract_id"
        csvName="deal_calculation"
        onRowClick={setSel}
      />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl" data-testid="deal-detail-modal">
          <DialogHeader><DialogTitle>Deal {sel?.trade_no} — {sel?.commodity}</DialogTitle></DialogHeader>
          {sel && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                ["Trader / Buyer", sel.trader], ["Quantity", `${fmtNum(sel.quantity)} MT`],
                ["Sales Contract", sel.sales_contract_number], ["Purchase Contract", sel.purchase_contract_number],
                ["Sale Price", fmtMoney(sel.sale_price)], ["Purchase Price", fmtMoney(sel.purchase_price)],
                ["Total Sales Value", fmtMoney(sel.total_sales_contract_value)], ["Total Purchase Value", fmtMoney(sel.total_purchase_contract_value)],
                ["LC Number", sel.lc_number], ["Gross Margin", fmtMoney(sel.total_sales_contract_value - sel.total_purchase_contract_value)],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-slate-100 dark:border-slate-800 pb-2">
                  <p className="text-xs text-slate-400">{k}</p>
                  <p className="font-medium font-mono">{v}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
